'use client';
import React, { useState, useEffect } from 'react';
import { Building2, Wallet, CreditCard, TrendingDown, TrendingUp, Clock, Receipt } from 'lucide-react';
import { formatRupiah } from '@/lib/mockData';
import { getProjectsDB, getHutangListDB, getPiutangListDB, getPembayaranListDB } from '@/lib/supabaseData';
import CurrencyDisplay from '@/components/ui/CurrencyDisplay';

interface KpiItem {
  id: string;
  label: string;
  value: string;
  rawValue?: number;
  isNumeric: boolean;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  valueColor: string;
  sub: string;
  trend?: string;
  trendUp?: boolean;
}

export default function KpiCards() {
  const [kpiData, setKpiData] = useState<KpiItem[]>([
    { id: 'kpi-total-proyek', label: 'Total Proyek', value: '0', isNumeric: false, icon: <Building2 size={22} />, iconBg: 'bg-blue-50', iconColor: 'text-blue-600', valueColor: 'text-blue-700', sub: '' },
    { id: 'kpi-total-budget', label: 'Total Budget', value: formatRupiah(0), rawValue: 0, isNumeric: true, icon: <Wallet size={22} />, iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', valueColor: 'text-emerald-700', sub: '' },
    { id: 'kpi-total-tagihan', label: 'Total Tagihan', value: formatRupiah(0), rawValue: 0, isNumeric: true, icon: <Receipt size={22} />, iconBg: 'bg-amber-50', iconColor: 'text-amber-600', valueColor: 'text-amber-700', sub: '' },
    { id: 'kpi-total-hutang', label: 'Total Hutang', value: formatRupiah(0), rawValue: 0, isNumeric: true, icon: <CreditCard size={22} />, iconBg: 'bg-red-50', iconColor: 'text-red-500', valueColor: 'text-red-600', sub: '' },
    { id: 'kpi-total-piutang', label: 'Total Piutang', value: formatRupiah(0), rawValue: 0, isNumeric: true, icon: <TrendingUp size={22} />, iconBg: 'bg-teal-50', iconColor: 'text-teal-600', valueColor: 'text-teal-700', sub: '' },
    { id: 'kpi-net-debt', label: 'Net Debt', value: formatRupiah(0), rawValue: 0, isNumeric: true, icon: <TrendingDown size={22} />, iconBg: 'bg-purple-50', iconColor: 'text-purple-600', valueColor: 'text-purple-700', sub: '' },
    { id: 'kpi-cash-need', label: 'Cash Need 30 Hari', value: formatRupiah(0), rawValue: 0, isNumeric: true, icon: <Clock size={22} />, iconBg: 'bg-orange-50', iconColor: 'text-orange-600', valueColor: 'text-orange-700', sub: '' },
  ]);

  useEffect(() => {
    Promise.all([getProjectsDB(), getHutangListDB(), getPiutangListDB(), getPembayaranListDB()]).then(([projects, hutangList, piutangList, pembayaranList]) => {
      const activeStatuses = ['Berjalan', 'Persiapan'];
      const activeProjects = projects.filter((p) => activeStatuses.includes(p.status));
      const berjalanCount = projects.filter((p) => p.status === 'Berjalan').length;
      const persiapanCount = projects.filter((p) => p.status === 'Persiapan').length;
      const totalBudget = activeProjects.reduce((sum, p) => sum + p.budget, 0);

      const dibayarMap: Record<string, number> = {};
      pembayaranList.forEach((p) => {
        dibayarMap[p.hutangId] = (dibayarMap[p.hutangId] ?? 0) + p.nominal;
      });

      const totalTagihan = hutangList.reduce((sum, h) => sum + h.nilaiTagihan, 0);
      const totalHutang = hutangList.reduce((sum, h) => {
        const actualDibayar = dibayarMap[h.id] ?? 0;
        return sum + Math.max(0, h.nilaiTagihan - actualDibayar);
      }, 0);
      const overdueHutang = hutangList
        .filter((h) => h.status === 'Overdue')
        .reduce((sum, h) => {
          const actualDibayar = dibayarMap[h.id] ?? 0;
          return sum + Math.max(0, h.nilaiTagihan - actualDibayar);
        }, 0);

      const totalPiutang = piutangList.reduce((sum, p) => sum + p.sisaPiutang, 0);
      const belumJatuhTempoPiutang = piutangList.filter((p) => p.status === 'Belum Jatuh Tempo').reduce((sum, p) => sum + p.sisaPiutang, 0);

      const netDebt = totalHutang - totalPiutang;
      const cashNeed30 = totalHutang - totalPiutang;

      const netDebtColor = netDebt <= 0 ? 'text-teal-700' : 'text-red-600';
      const netDebtBg = netDebt <= 0 ? 'bg-teal-50' : 'bg-red-50';
      const netDebtIconColor = netDebt <= 0 ? 'text-teal-600' : 'text-red-500';
      const netDebtSub = netDebt <= 0 ? 'Piutang > Hutang' : 'Hutang > Piutang';

      const formatJuta = (val: number) => {
        const abs = Math.abs(val);
        if (abs >= 1000000) return `${abs >= 1000000000 ? (abs / 1000000000).toFixed(0) + 'M' : (abs / 1000000).toFixed(0) + 'jt'}`;
        return formatRupiah(abs);
      };

      setKpiData([
        { id: 'kpi-total-proyek', label: 'Total Proyek', value: String(activeProjects.length), isNumeric: false, icon: <Building2 size={22} />, iconBg: 'bg-blue-50', iconColor: 'text-blue-600', valueColor: 'text-blue-700', sub: `${berjalanCount} Berjalan · ${persiapanCount} Persiapan` },
        { id: 'kpi-total-budget', label: 'Total Budget', value: formatRupiah(totalBudget), rawValue: totalBudget, isNumeric: true, icon: <Wallet size={22} />, iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', valueColor: 'text-emerald-700', sub: `${activeProjects.length} Proyek Aktif` },
        { id: 'kpi-total-tagihan', label: 'Total Tagihan', value: formatRupiah(totalTagihan), rawValue: totalTagihan, isNumeric: true, icon: <Receipt size={22} />, iconBg: 'bg-amber-50', iconColor: 'text-amber-600', valueColor: 'text-amber-700', sub: `${hutangList.length} Invoice` },
        { id: 'kpi-total-hutang', label: 'Total Hutang', value: formatRupiah(totalHutang), rawValue: totalHutang, isNumeric: true, icon: <CreditCard size={22} />, iconBg: 'bg-red-50', iconColor: 'text-red-500', valueColor: 'text-red-600', sub: overdueHutang > 0 ? `${formatJuta(overdueHutang)} Overdue` : 'Tidak ada overdue' },
        { id: 'kpi-total-piutang', label: 'Total Piutang', value: formatRupiah(totalPiutang), rawValue: totalPiutang, isNumeric: true, icon: <TrendingUp size={22} />, iconBg: 'bg-teal-50', iconColor: 'text-teal-600', valueColor: 'text-teal-700', sub: belumJatuhTempoPiutang > 0 ? `${formatJuta(belumJatuhTempoPiutang)} Belum JT` : 'Semua jatuh tempo' },
        { id: 'kpi-net-debt', label: 'Net Debt', value: formatRupiah(netDebt), rawValue: netDebt, isNumeric: true, icon: <TrendingDown size={22} />, iconBg: netDebtBg, iconColor: netDebtIconColor, valueColor: netDebtColor, sub: netDebtSub },
        { id: 'kpi-cash-need', label: 'Cash Need 30 Hari', value: formatRupiah(cashNeed30), rawValue: cashNeed30, isNumeric: true, icon: <Clock size={22} />, iconBg: 'bg-orange-50', iconColor: 'text-orange-600', valueColor: 'text-orange-700', sub: 'Proyeksi 30 hari' },
      ]);
    });
  }, []);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
      {kpiData?.map((kpi) => (
        <div
          key={kpi?.id}
          className="bg-white rounded-2xl p-4 flex flex-col gap-3 hover:shadow-lg transition-all duration-200 fade-in min-w-0 overflow-hidden"
          style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)' }}
        >
          {/* Icon + menu */}
          <div className="flex items-center justify-between">
            <div className={`w-10 h-10 rounded-xl ${kpi?.iconBg} flex items-center justify-center flex-shrink-0`}>
              <span className={kpi?.iconColor}>{kpi?.icon}</span>
            </div>
            <button className="text-gray-300 hover:text-gray-500 transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
              </svg>
            </button>
          </div>

          {/* Value */}
          <div className="min-w-0">
            <p className="text-xs font-500 text-gray-400 tracking-wide truncate mb-1">{kpi?.label}</p>
            {kpi?.isNumeric && kpi?.rawValue !== undefined ? (
              <CurrencyDisplay
                value={kpi.rawValue}
                className={`text-sm font-800 ${kpi?.valueColor} leading-tight w-full`}
              />
            ) : (
              <p className={`text-2xl font-800 tabular-nums ${kpi?.valueColor} leading-tight`}>{kpi?.value}</p>
            )}
          </div>

          {/* Sub info */}
          <p className="text-xs text-gray-400 leading-tight line-clamp-2 mt-auto">{kpi?.sub}</p>
        </div>
      ))}
    </div>
  );
}